// src/components/sections/ApplicationSection.jsx
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import applicationbg from "../../assets/background/applicationbg.png";

import egoverment from "../../assets/img/egoverment.jpg";
import ekyc from "../../assets/img/ekyc.jpg";
import docsign from "../../assets/img/docsign.jpg";
import bankfinance from "../../assets/img/bankfinance.jpg";

import CarouselControls from "../common/CarouselControls";

const SCREENS = {
  mobile: { min: 0, max: 767 },
  tabletmini: { min: 768, max: 792 },
  tabletair: { min: 793, max: 820 },
  tabletpro: { min: 821, max: 1024 },
  desktop: { min: 1025, max: Infinity },
};

const LAYOUTS = {
  mobile: {
    center: { w: 360, h: 240, scale: 1 },
    // near:   { w: 240, h: 140, scale: 0.95 },
    // far:    { w: 200, h: 118, scale: 0.9 },
    shift: { "-2": -140, "-1": -75, "0": 0, "1": 75, "2": 140 },
  },
  tabletmini: {
    center: { w: 420, h: 230, scale: 1 },
    near: { w: 340, h: 186, scale: 0.95 },
    far: { w: 280, h: 154, scale: 0.9 },
    shift: { "-2": -190, "-1": -120, "0": 0, "1": 120, "2": 190 },
  },
  tabletair: {
    center: { w: 470, h: 230, scale: 1 },
    near: { w: 380, h: 182, scale: 0.95 },
    far: { w: 310, h: 145, scale: 0.9 },
    shift: { "-2": -200, "-1": -125, "0": 0, "1": 125, "2": 200 },
  },
  tabletpro: {
    center: { w: 630, h: 310, scale: 1 },
    near: { w: 490, h: 236, scale: 0.95 },
    far: { w: 390, h: 184, scale: 0.9 },
    shift: { "-2": -280, "-1": -170, "0": 0, "1": 170, "2": 280 },
  },
  desktop: {
    center: { w: 800, h: 400, scale: 1 },
    near: { w: 640, h: 320, scale: 0.95 },
    far: { w: 480, h: 240, scale: 0.9 },
    shift: { "-2": -365, "-1": -220, "0": 0, "1": 220, "2": 365 },
  },
};

function matchScreenLabel(width) {
  if (width >= SCREENS.desktop.min) return "desktop";
  if (width >= SCREENS.tabletpro.min && width <= SCREENS.tabletpro.max) return "tabletpro";
  if (width >= SCREENS.tabletair.min && width <= SCREENS.tabletair.max) return "tabletair";
  if (width >= SCREENS.tabletmini.min && width <= SCREENS.tabletmini.max) return "tabletmini";
  return "mobile";
}

function useScreenLabel() {
  const get = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1920;
    return matchScreenLabel(w);
  };
  const [label, setLabel] = React.useState(get);
  React.useEffect(() => {
    const onResize = () => setLabel(get());
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return label;
}

function useLayout() {
  const label = useScreenLabel();
  return { label, layout: LAYOUTS[label] || LAYOUTS.desktop };
}

export default function ApplicationSection() {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef(null);

  const slides = useMemo(
    () => [
      { key: "egov", img: egoverment },
      { key: "ekyc", img: ekyc },
      { key: "docsign", img: docsign },
      { key: "bank", img: bankfinance },
    ],
    []
  );

  const total = slides.length;
  const prev = () => setIdx((p) => (p - 1 + total) % total);
  const next = () => setIdx((p) => (p + 1) % total);

  // Swipe
  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next() : prev());
    touchStartX.current = null;
  };

  const { label, layout } = useLayout();
  const visibleOffsets = label === "mobile" ? [0] : [-2, -1, 0, 1, 2];

  const sizeFor = (off) => {
    if (off === 0) return layout.center;
    if (Math.abs(off) === 1) return layout.near;
    return layout.far;
  };

  const shiftFor = (off) => layout.shift[String(off)] ?? 0;

  // Overlay gradient (inline style)
  const overlayStyle = (off) => {
    const endAlpha = off === 0 ? 0.7 : Math.abs(off) === 1 ? 0.75 : 0.8;
    return {
      background: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,${endAlpha}) 100%)`,
      transition: "background 800ms cubic-bezier(0.22, 1, 0.36, 1)",
    };
  };

  const captionOpacity = (off) => (off === 0 ? 1 : Math.abs(off) === 1 ? 0.6 : 0.4);
  const zIndexFor = (off) => (off === 0 ? 50 : Math.abs(off) === 1 ? 30 : 20);

  const ease = "cubic-bezier(0.22, 1, 0.36, 1)"; // easeOutCubic

  const captionFontSize = (off) => {
    if (label === "mobile") return off === 0 ? "16px" : "10px";
    if (label === "tabletmini") return off === 0 ? "16px" : "12px";
    if (label === "tabletair") return off === 0 ? "16px" : "14px";
    if (label === "tabletpro") return off === 0 ? "20px" : "16px";
    return off === 0 ? "24px" : "18px";
  };

  const captionSubFontSize = (off) => {
    if (label === "mobile") return off === 0 ? "14px" : "10px";
    if (label === "tabletmini") return off === 0 ? "14px" : "10px";
    if (label === "tabletair") return off === 0 ? "14px" : "12px";
    if (label === "tabletpro") return off === 0 ? "14px" : "12px";
    return off === 0 ? "16px" : "14px";
  };

  // Ép mọi kiểu về mảng để .map không lỗi
  const toArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      return val
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

  return (
    <section
      id="apps"
      className="relative isolate text-white bg-cover bg-center bg-no-repeat px-4 py-36 tabletair:py-16 tabletmini:py-16 mobile:py-10 mobile:px-4 tabletpro:px-10 tabletair:px-10 tabletmini:px-10"
      style={{ backgroundImage: `url(${applicationbg})` }}
    >
      <div className="max-w-[1400px] mx-auto flex flex-col gap-[48px]">
        {/* Headline */}
        <div className="flex flex-col gap-[16px] mx-auto text-center">
          <p className="text-[20px] mobile:text-[14px] font-medium text-brandBlue-500 tracking-wide uppercase">
            {t("applications.caption", { defaultValue: "ỨNG DỤNG" })}
          </p>
          <h2 className="text-[56px] font-semibold mobile:text-[24px] tabletpro:text-[56px] tabletair:text-[36px] tabletmini:text-[32px] text-neutral-1 font-md max-w-[1050px] mx-auto">
            {t("applications.headline", {
              defaultValue: "Quantera - Hạ tầng định danh số liên thông & bảo mật",
            })}
          </h2>
          <p className="text-neutral-5 font-normal max-w-[1010px] mx-auto text-[16px] mobile:text-[16px] tabletpro:text-[16px] tabletair:text-[16px] tabletmini:text-[16px]">
            {t("applications.sub", {
              defaultValue:
                "Ứng dụng hỗ trợ định danh số, eKYC, ký số và lưu trữ hồ sơ điện tử, phục vụ đa lĩnh vực như chính phủ điện tử, ngân hàng, tài chính, viễn thông, giúp xác minh an toàn, giảm gian lận và mở rộng dịch vụ số.",
            })}
          </p>
        </div>

        {/* Carousel */}
        <div className="relative max-w-[1400px] mx-auto select-none">
          <div
            className="relative h-[500px] tabletpro:h-[360px] tabletair:h-[320px] tabletmini:h-[280px] mobile:h-[240px]"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {visibleOffsets.map((off) => {
              const i = (idx + off + total) % total;
              const s = slides[i];
              const { w, h, scale } = sizeFor(off);
              const shift = shiftFor(off);
              const z = zIndexFor(off);

              // Chỉ hiện caption khi off ∈ {-2, -1, 0}
              const showCaption = off === 0 || off === -1 || off === -2;

              return (
                <div
                  key={s.key}
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: `translate(-50%, -50%) translateX(${shift}px) scale(${scale})`,
                    transition: `transform 800ms ${ease}`,
                    zIndex: z,
                    pointerEvents: off === 0 ? "auto" : "none",
                    willChange: "transform",
                  }}
                  aria-hidden={off !== 0}
                >
                  <figure
                    className="relative overflow-hidden rounded-[28px] shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
                    style={{
                      width: w,
                      height: h,
                      transition: `width 800ms ${ease}, height 800ms ${ease}`,
                    }}
                  >
                    <img
                      src={s.img}
                      alt={t(`applications.slides.${s.key}.title`, { defaultValue: "" })}
                      className="w-full h-full object-cover"
                      draggable={false}
                      decoding="async"
                    />

                    {/* Overlay gradient */}
                    <div className="absolute inset-0" style={overlayStyle(off)} />

                    {/* Caption */}
                    {showCaption && (
                      <figcaption
                        className="absolute left-0 right-0 bottom-0 p-6 text-white"
                        style={{
                          opacity: captionOpacity(off),
                          transition: `opacity 800ms ${ease}`,
                        }}
                      >
                        <h3
                          className="font-semibold"
                          style={{
                            fontSize: captionFontSize(off),
                            transition: `font-size 800ms ${ease}`,
                          }}
                        >
                          {t(`applications.slides.${s.key}.title`)}
                        </h3>

                        {(() => {
                          const rawBullets = t(`applications.slides.${s.key}.bullets`, {
                            returnObjects: true,
                            defaultValue: [],
                          });
                          const bullets = toArray(rawBullets);
                          if (!bullets.length) return null;

                          return (
                            <ul
                              className="mt-2 list-disc market:text-[10px] list-outside pl-5 text-[#D4D4D4] space-y-1"
                              style={{
                                fontSize: captionSubFontSize(off),
                                transition: `font-size 800ms ${ease}`,
                              }}
                            >
                              {bullets.map((line, li) => (
                                <li key={li}>{line}</li>
                              ))}
                            </ul>
                          );
                        })()}
                      </figcaption>
                    )}
                  </figure>
                </div>
              );
            })}
          </div>

          {/* Controls */}
          <CarouselControls onPrev={prev} onNext={next} />
        </div>
      </div>
    </section>
  );
}
